<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent>
            <DialogHeader class="items-center">
                <template #icon> <RaynetLogo /> </template>
                <DialogTitle>
                    {{
                        integration
                            ? $t("integrations.raynet.modal.title_edit")
                            : $t("integrations.raynet.modal.title_add")
                    }}</DialogTitle
                >
            </DialogHeader>

            <div class="mt-2 px-6">
                <div class="mt-4 gap-1.5 grid w-full items-center">
                    <Label for="raynet-user">{{
                        $t("integrations.raynet.modal.username_label")
                    }}</Label>

                    <div class="relative items-center">
                        <Input
                            v-model="user"
                            type="email"
                            id="icloud-mail"
                            class="pl-10"
                        />
                        <span
                            class="inset-y-0 start-0 px-3 absolute flex items-center justify-center"
                        >
                            <Mail class="size-6 text-iron-500" />
                        </span>
                    </div>
                </div>

                <div class="mt-4 gap-1.5 grid w-full items-center">
                    <Label for="raynet-instance">{{
                        $t("integrations.raynet.modal.instance_label")
                    }}</Label>

                    <InputWithPrefix
                        v-model="instance"
                        id="raynet-instance"
                        placeholder="vase-firma"
                    >
                        <template #prefix>
                            <span
                                class="h-10 rounded-l-md border-iron-300 bg-iron-50 px-3 text-iron-600 sm:text-sm inline-flex shrink-0 items-center border border-r-0"
                                >app.raynet.cz/</span
                            >
                        </template>
                    </InputWithPrefix>
                </div>

                <div class="mt-4 gap-1.5 grid w-full items-center">
                    <Label for="raynet-pass">{{
                        $t("integrations.raynet.modal.api_key_label")
                    }}</Label>
                    <div class="relative items-center">
                        <Input
                            v-model="password"
                            type="email"
                            id="icloud-mail"
                            class="pl-10"
                        />
                        <span
                            class="inset-y-0 start-0 px-3 absolute flex items-center justify-center"
                        >
                            <KeyRound class="size-6 text-iron-500" />
                        </span>
                    </div>
                    <p class="text-sm text-iron-500 tracking-[-0.01em]">
                        Návod k umístění API klíčů najdete v
                        <a
                            href="https://podpora.raynet.cz/hc/cs/articles/360032478072-Jak-vygenerovat-API-kl%C3%AD%C4%8D"
                            class="font-medium text-z-primary-600 hover:text-z-primary-700"
                            target="_blank"
                        >
                            Raynet nápovědě.
                        </a>
                    </p>
                </div>

                <div class="mt-4 gap-1.5 grid w-full items-center">
                    <Label for="owner">{{
                        $t("integrations.raynet.modal.owner_label")
                    }}</Label>
                    <Input v-model="owner" type="number" id="owner" />
                </div>
            </div>
            <DialogFooter
                class="w-full"
                :class="cn(removable ? '!justify-between' : 'justify-end')"
            >
                <Button
                    v-if="removable"
                    @click="$emit('remove-integration')"
                    variant="destructive-ghost"
                    class="gap-1 flex"
                    size="xs"
                >
                    <Trash :size="16" />
                    {{ $t("integrations.remove_integration") }}
                </Button>

                <div class="flex-end flex">
                    <DialogClose>
                        <Button class="ml-4" variant="outline" type="button">{{
                            $t("cancel")
                        }}</Button>
                    </DialogClose>

                    <Button
                        class="ml-4"
                        type="button"
                        @click="createOrUpdateRaynetIntegration"
                        >{{
                            integration ? $t("save_changes") : $t("add")
                        }}</Button
                    >
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { cn } from "@/lib/utils";
import { ref } from "vue";
import { router } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import { KeyRound, Mail, Trash } from "lucide-vue-next";
import RaynetLogo from "../logos/simple/Raynet.vue";
import route from "ziggy-js";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import InputWithPrefix from "@/components/ui/input-with-prefix/InputWithPrefix.vue";

const props = defineProps<{
    integration?: any;
    removable?: boolean;
}>();

const instance = ref(props.integration ? props.integration.data.instance : "");
const user = ref(props.integration ? props.integration.data.user : "");
const password = ref(props.integration ? props.integration.data.password : "");
const owner = ref(props.integration ? props.integration.data.owner : "");
const opened = ref(false);

function create() {
    router.post(
        route("app.raynet-integrations.store"),
        {
            instance: instance.value,
            user: user.value,
            password: password.value,
            owner: owner.value,
        },
        {
            onFinish: () => {
                closeModal();
            },
        },
    );
}

function update() {
    router.put(
        route("app.raynet-integrations.update", {
            integration: props.integration?.id,
        }),
        {
            instance: instance.value,
            user: user.value,
            password: password.value,
            owner: owner.value,
        },
        {
            onFinish: () => {
                closeModal();
            },
        },
    );
}

function createOrUpdateRaynetIntegration() {
    if (props.integration) {
        update();
    } else {
        create();
    }
}

const closeModal = () => {
    opened.value = false;
};
</script>

<style></style>
